import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Plus, Trash2, PackagePlus, Save, AlertTriangle, ChefHat, Boxes, History,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const RawMaterials = () => {
  const [materials, setMaterials] = useState([]);
  const [recipe, setRecipe] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('materials'); // 'materials' | 'recipe'

  // create material dialog
  const [createOpen, setCreateOpen] = useState(false);
  const emptyNew = { name: '', unit: 'kg', current_stock: 0, low_threshold: 0, notes: '' };
  const [newMat, setNewMat] = useState(emptyNew);

  // stock-in dialog
  const [stockMat, setStockMat] = useState(null);
  const [stockQty, setStockQty] = useState('');
  const [stockNotes, setStockNotes] = useState('');

  // edit material dialog
  const [editMat, setEditMat] = useState(null);

  // recipe edit (dirty tracking)
  const [recipeDirty, setRecipeDirty] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [m, r] = await Promise.all([
        axios.get(`${API}/materials`, { headers: auth() }),
        axios.get(`${API}/materials/recipe`, { headers: auth() }),
      ]);
      setMaterials(m.data.materials || []);
      setRecipe(r.data.items || []);
      setRecipeDirty(false);
    } catch (e) {
      toast.error('Failed to load materials');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newMat.name.trim()) return toast.error('Name is required');
    try {
      await axios.post(`${API}/materials`, newMat, { headers: auth() });
      toast.success(`Added ${newMat.name}`);
      setCreateOpen(false);
      setNewMat(emptyNew);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add');
    }
  };

  const handleStockIn = async () => {
    const qty = parseFloat(stockQty);
    if (!qty || qty <= 0) return toast.error('Quantity must be positive');
    try {
      await axios.post(`${API}/materials/${stockMat.id}/stock-in`, { material_id: stockMat.id, quantity: qty, notes: stockNotes }, { headers: auth() });
      toast.success(`Received ${qty}${stockMat.unit} of ${stockMat.name}`);
      setStockMat(null); setStockQty(''); setStockNotes('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  const handleDelete = async (mat) => {
    if (!window.confirm(`Delete material "${mat.name}"? (Will also be removed from recipe.)`)) return;
    try {
      await axios.delete(`${API}/materials/${mat.id}`, { headers: auth() });
      toast.success(`Deleted ${mat.name}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  const handleEdit = async () => {
    if (!editMat) return;
    try {
      await axios.put(`${API}/materials/${editMat.id}`, {
        name: editMat.name,
        unit: editMat.unit,
        low_threshold: parseFloat(editMat.low_threshold) || 0,
        notes: editMat.notes,
      }, { headers: auth() });
      toast.success('Updated');
      setEditMat(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  // Recipe handlers
  const toggleRecipeItem = (materialId, materialName) => {
    setRecipeDirty(true);
    const existing = recipe.find((r) => r.material_id === materialId);
    if (existing) setRecipe(recipe.filter((r) => r.material_id !== materialId));
    else setRecipe([...recipe, { material_id: materialId, material_name: materialName, quantity_per_batch: 0 }]);
  };

  const updateRecipeQty = (materialId, qty) => {
    setRecipeDirty(true);
    setRecipe(recipe.map((r) => r.material_id === materialId ? { ...r, quantity_per_batch: parseFloat(qty) || 0 } : r));
  };

  const saveRecipe = async () => {
    if (recipe.some((r) => r.quantity_per_batch <= 0)) {
      return toast.error('All recipe quantities must be positive');
    }
    try {
      await axios.put(`${API}/materials/recipe`, { items: recipe }, { headers: auth() });
      toast.success('Recipe saved');
      setRecipeDirty(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  if (loading) return <div className="p-6 text-gray-500">Loading materials...</div>;

  const lowStockList = materials.filter((m) => m.is_low && m.low_threshold > 0);

  return (
    <div className="space-y-4">
      {lowStockList.length > 0 && (
        <Card className="border-amber-300 bg-amber-50">
          <CardContent className="p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
            <div className="text-sm text-amber-900">
              <strong>Low stock:</strong> {lowStockList.map((m) => `${m.name} (${m.current_stock}${m.unit})`).join(', ')}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="flex gap-2">
        <button
          data-testid="tab-mats"
          onClick={() => setTab('materials')}
          className={`px-4 py-2 rounded-lg font-medium ${tab === 'materials' ? 'bg-[#1A4D2E] text-white' : 'bg-white border border-gray-200 text-gray-700'}`}
        >
          <Boxes className="w-4 h-4 inline mr-2" />Materials ({materials.length})
        </button>
        <button
          data-testid="tab-recipe"
          onClick={() => setTab('recipe')}
          className={`px-4 py-2 rounded-lg font-medium ${tab === 'recipe' ? 'bg-[#1A4D2E] text-white' : 'bg-white border border-gray-200 text-gray-700'}`}
        >
          <ChefHat className="w-4 h-4 inline mr-2" />Batch Recipe ({recipe.length})
        </button>
      </div>

      {tab === 'materials' && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Raw Materials</CardTitle>
            <Button onClick={() => setCreateOpen(true)} className="bg-[#1A4D2E] hover:bg-[#143D23] text-white" data-testid="new-material-btn">
              <Plus className="w-4 h-4 mr-2" />Add Material
            </Button>
          </CardHeader>
          <CardContent>
            {materials.length === 0 && <p className="text-gray-500">No materials yet. Click &quot;Add Material&quot;.</p>}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left border-b border-gray-200 text-gray-500"><th className="py-2 pr-4">Name</th><th className="py-2 pr-4">Current Stock</th><th className="py-2 pr-4">Low Threshold</th><th className="py-2 pr-4">Status</th><th className="py-2 pr-4">Actions</th></tr></thead>
                <tbody>
                  {materials.map((m) => (
                    <tr key={m.id} className="border-b border-gray-100">
                      <td className="py-3 pr-4 font-medium">{m.name}</td>
                      <td className="py-3 pr-4">{m.current_stock} {m.unit}</td>
                      <td className="py-3 pr-4 text-gray-600">{m.low_threshold} {m.unit}</td>
                      <td className="py-3 pr-4">
                        {m.is_low && m.low_threshold > 0
                          ? <Badge className="bg-amber-100 text-amber-800">Low</Badge>
                          : <Badge className="bg-green-100 text-green-800">OK</Badge>}
                      </td>
                      <td className="py-3 pr-4">
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" onClick={() => { setStockMat(m); setStockQty(''); setStockNotes(''); }} title="Receive stock"><PackagePlus className="w-3.5 h-3.5" /></Button>
                          <Button size="sm" variant="outline" onClick={() => setEditMat({ ...m })} title="Edit"><History className="w-3.5 h-3.5" /></Button>
                          <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleDelete(m)} title="Delete"><Trash2 className="w-3.5 h-3.5" /></Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {tab === 'recipe' && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Batch Recipe — required per compost batch</CardTitle>
            <Button onClick={saveRecipe} disabled={!recipeDirty} className="bg-[#1A4D2E] hover:bg-[#143D23] text-white" data-testid="save-recipe">
              <Save className="w-4 h-4 mr-2" />Save Recipe
            </Button>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-600 mb-3">
              Pick the materials and quantities used to make one batch. When you create a compost batch, these quantities are deducted from stock; if any falls below the threshold, the system creates a low-stock notification. Shortages produce a soft warning but the batch is still created.
            </p>
            {materials.length === 0 ? (
              <p className="text-gray-500">Add materials first, then come back to set the recipe.</p>
            ) : (
              <div className="space-y-2">
                {materials.map((m) => {
                  const inRecipe = recipe.find((r) => r.material_id === m.id);
                  return (
                    <div key={m.id} className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg">
                      <input
                        type="checkbox"
                        checked={!!inRecipe}
                        onChange={() => toggleRecipeItem(m.id, m.name)}
                        data-testid={`recipe-toggle-${m.name}`}
                      />
                      <div className="flex-1 font-medium">{m.name} <span className="text-xs text-gray-500">({m.unit})</span></div>
                      {inRecipe && (
                        <div className="flex items-center gap-2">
                          <Label className="text-xs">Qty per batch</Label>
                          <Input
                            type="number"
                            min={0}
                            step="any"
                            className="w-32"
                            value={inRecipe.quantity_per_batch}
                            onChange={(e) => updateRecipeQty(m.id, e.target.value)}
                          />
                          <span className="text-sm text-gray-500">{m.unit}</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Create material */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Raw Material</DialogTitle>
            <DialogDescription>e.g. Wheat Straw, Gypsum, Chicken Manure, Urea, Spawn.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-3">
            <div><Label>Name *</Label><Input value={newMat.name} onChange={(e) => setNewMat({ ...newMat, name: e.target.value })} required data-testid="mat-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Unit</Label>
                <select value={newMat.unit} onChange={(e) => setNewMat({ ...newMat, unit: e.target.value })} className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg bg-white">
                  <option>kg</option><option>g</option><option>L</option><option>ml</option><option>bags</option><option>units</option>
                </select>
              </div>
              <div><Label>Current Stock</Label><Input type="number" min={0} step="any" value={newMat.current_stock} onChange={(e) => setNewMat({ ...newMat, current_stock: parseFloat(e.target.value) || 0 })} /></div>
            </div>
            <div><Label>Low-stock threshold (notify when at or below)</Label><Input type="number" min={0} step="any" value={newMat.low_threshold} onChange={(e) => setNewMat({ ...newMat, low_threshold: parseFloat(e.target.value) || 0 })} data-testid="mat-threshold" /></div>
            <div><Label>Notes</Label><Input value={newMat.notes} onChange={(e) => setNewMat({ ...newMat, notes: e.target.value })} /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-[#1A4D2E] hover:bg-[#143D23] text-white" data-testid="mat-submit">Add</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Stock in */}
      <Dialog open={!!stockMat} onOpenChange={(o) => { if (!o) { setStockMat(null); setStockQty(''); setStockNotes(''); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Receive Stock — {stockMat?.name}</DialogTitle>
            <DialogDescription>Current stock: <strong>{stockMat?.current_stock} {stockMat?.unit}</strong></DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Quantity received ({stockMat?.unit})</Label><Input type="number" min={0} step="any" value={stockQty} onChange={(e) => setStockQty(e.target.value)} autoFocus data-testid="stockin-qty" /></div>
            <div><Label>Notes (supplier, invoice no, etc.)</Label><Input value={stockNotes} onChange={(e) => setStockNotes(e.target.value)} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStockMat(null)}>Cancel</Button>
            <Button onClick={handleStockIn} className="bg-[#1A4D2E] hover:bg-[#143D23] text-white" data-testid="stockin-submit">Add Stock</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit material */}
      <Dialog open={!!editMat} onOpenChange={(o) => { if (!o) setEditMat(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Material</DialogTitle>
          </DialogHeader>
          {editMat && (
            <div className="space-y-3">
              <div><Label>Name</Label><Input value={editMat.name} onChange={(e) => setEditMat({ ...editMat, name: e.target.value })} /></div>
              <div><Label>Unit</Label><Input value={editMat.unit} onChange={(e) => setEditMat({ ...editMat, unit: e.target.value })} /></div>
              <div><Label>Low-stock threshold</Label><Input type="number" min={0} step="any" value={editMat.low_threshold} onChange={(e) => setEditMat({ ...editMat, low_threshold: e.target.value })} /></div>
              <div><Label>Notes</Label><Input value={editMat.notes || ''} onChange={(e) => setEditMat({ ...editMat, notes: e.target.value })} /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditMat(null)}>Cancel</Button>
            <Button onClick={handleEdit} className="bg-[#1A4D2E] hover:bg-[#143D23] text-white">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RawMaterials;
